/**
 * AddToCart — quantity stepper + add-to-cart button for product tiles.
 *
 * Handles all product type branching:
 * - Standard/variant: direct add to cart
 * - Master (on tile, not in quick view): opens quick view instead
 * - Bundle without variations: direct add with bundled child data
 * - Set: "Add All to Cart" text
 * - OOS override: disabled "Sold Out" button
 *
 * Ported from:
 * - hec-composable/hec/components/add-to-cart/addToCart.tsx
 * - sfcc-website/org_hec/cartridge/templates/default/product/components/addToCartGlobalPlp.isml
 * - sfcc-website/org_hec/cartridge/templates/default/product/components/quantity.isml
 */
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { Button } from "~/components/ui/button";
import { QuantityStepper } from "~/hec/components/ui/quantity-stepper";
import type { HecProduct } from "~/hec/types/product";
import {
  isBundleProduct,
  isMasterProduct,
  isOutOfStockOverride,
  isSetProduct,
} from "~/hec/types/product";
import { resolveQuantityCap } from "~/lib/quantity-cap";
import {
  useBasketActions,
  useBasketLineQuantityReader,
  useBasketReconcile,
} from "~/routes/resources/hooks/basket-hooks";
import {
  type BasketAddOutcome,
  type BasketAddOutcomeContext,
  useBasketAddReconcile,
} from "~/routes/resources/hooks/use-basket-add-reconcile";
import { useFetcherDeadline } from "~/routes/resources/hooks/use-fetcher-deadline";
import { reportClientError } from "~/utils/report-client-error";

/**
 * Deadline for a tile / recs-carousel add that never settles (dev-regression
 * triage 2026-09-09, symptom 1). A healthy add round-trips in ~0.2-0.8s. Past
 * it the submission is treated as hung and beaconed under `basket-add`. What
 * happens next depends on which half hung (`useFetcherDeadline`):
 *
 *   - "submit" — the POST itself is still open. An add is NOT idempotent and
 *     the Worker keeps working after the client gives up (`upstream-timeouts.ts`
 *     CANCEL_ON_DISCONNECT excludes SCAPI on purpose), so at 10s the outcome is
 *     genuinely UNKNOWN — and it stays unknown for the action's whole bounded
 *     lifetime (~66s, derived in `use-basket-add-reconcile.ts`), not for one 8s
 *     SCAPI budget. The tile therefore enters the uncertain-state protocol:
 *     button disabled with confirming copy, fetcher kept alive, basket polled
 *     for the write inside a 90s window, resolved as `confirmed-late` /
 *     `rejected-late` / `unconfirmed` (v2026.15.02 review, P1 round 2). No copy
 *     on this path ever says "try again" — that is the double-add path.
 *   - "revalidation" — the add landed and only the post-action reload is
 *     open: re-enable silently (the modal already shows the item), beacon only.
 *
 * Proper fix (follow-up, not hotfix): an idempotency key on the add action.
 */
export const BASKET_ADD_DEADLINE_MS = 10_000;
export const BASKET_ADD_BEACON_SCOPE = "basket-add";
/** While the protocol runs: the toast, and the sr-only status on the tile. */
export const BASKET_ADD_CONFIRMING_COPY =
  "Your cart is taking longer than usual to update. We're confirming whether this item was added.";
/** Terminal `unconfirmed` — the window elapsed without seeing the write. */
export const BASKET_ADD_TIMEOUT_COPY =
  "Your cart is taking longer than usual to update. Please check your cart before adding this item again.";
/** Terminal `confirmed-late` found by a poll. (A late RESPONSE opens the
 *  normal added-to-basket modal instead, via the layout's fetcher watcher.) */
export const BASKET_ADD_CONFIRMED_COPY = "Added to your cart.";

/** One toast per product, updated in place as the protocol moves through its
 *  states (loading → success / error / dismissed). */
const basketAddToastId = (productId: string) =>
  `${BASKET_ADD_BEACON_SCOPE}:${productId}`;

interface AddToCartProps {
  product: HecProduct;
  /** When true on a tile, master products open quick view instead of direct ATC */
  isProductTile?: boolean;
  /** Callback to open quick view (for master products on tile). Receives the
   *  stepper's current quantity so the quick view can pre-fill it (SFCC-11825). */
  onOpenQuickView?: (quantity: number) => void;
  /** Whether the component is inside a quick view modal already */
  isInQuickView?: boolean;
  /** Show "In Stock" text below the button */
  showStockStatus?: boolean;
  /** Forwarded to QuantityStepper. Hides "−" at qty=1 to save space in
   *  narrow placements (e.g. ATC modal carousel tiles). */
  collapseStepperAtMin?: boolean;
  /** Render a compact "Add" label at every width instead of the
   *  wrap-and-clip "Add To Cart" — keeps the CTA legible on narrow recs
   *  tiles (e.g. the cart-items-column recs carousel). */
  compactAddLabel?: boolean;
  /** Breakpoint-driven label (vs. the default width-based wrap-and-clip):
   *  show just "Add" below the `lg` breakpoint and the full "Add To Cart"
   *  from `lg` up. Used by the favorites list rows. */
  fullLabelFromLg?: boolean;
  /** Seed the stepper's initial quantity (COM-13 — favorites list rows share
   *  one control between the persisted qty editor and ATC, SFRA parity:
   *  `wishlist.js:627`). Precedent: `ProductQuickView.initialQuantity`. */
  initialQuantity?: number;
  /** Notified on every stepper change, in addition to local state — lets a
   *  parent row persist the value (COM-13). Additive; no other call site
   *  needs to pass it. */
  onQuantityChange?: (quantity: number) => void;
  /** Hide the quantity stepper, keep the (disabled, when OOS) ATC button —
   *  SFRA favorites-row parity: `productListItemProductPrice.isml` wraps
   *  only the qty selector in `<isif condition=${item.available}>`; the ATC
   *  button always renders and just widens its column. */
  hideQuantity?: boolean;
}

export function AddToCart({
  product,
  isProductTile = false,
  onOpenQuickView,
  isInQuickView = false,
  showStockStatus = false,
  collapseStepperAtMin = false,
  compactAddLabel = false,
  fullLabelFromLg = false,
  initialQuantity,
  onQuantityChange,
  hideQuantity = false,
}: AddToCartProps) {
  const basket = useBasketActions();
  const reconcileBasket = useBasketReconcile();
  const readLineQuantity = useBasketLineQuantityReader();
  const [quantity, setQuantity] = useState(initialQuantity ?? 1);
  // Re-sync when the caller's `initialQuantity` CHANGES (not just on mount) —
  // needed for COM-13's favorites row: a failed persist PATCH rolls back by
  // handing this a new (reverted) value, and without this effect the visible
  // stepper would keep showing the failed value (the toast says it failed,
  // the number on screen doesn't move) and the ATC button would submit the
  // wrong quantity. `ProductQuickView`'s one-time seed usage is unaffected —
  // it never changes `initialQuantity` after the dialog opens.
  useEffect(() => {
    if (initialQuantity !== undefined) setQuantity(initialQuantity);
  }, [initialQuantity]);

  const productIdForBeacon = product.id || product.productId || "";

  // Terminal states of the uncertain-add protocol. Every one is beaconed
  // (scope `basket-add`, `phase` = outcome) so the production rate of
  // late-landing writes is measurable; the UI reaction is per outcome.
  const onAddOutcome = useCallback(
    (outcome: BasketAddOutcome, ctx: BasketAddOutcomeContext) => {
      reportClientError(
        BASKET_ADD_BEACON_SCOPE,
        new Error(
          `basket add ${outcome} after ${ctx.elapsedMs}ms (${ctx.source}, ${ctx.polls} polls)`,
        ),
        {
          productId: ctx.productId,
          phase: outcome,
          source: ctx.source,
          elapsedMs: ctx.elapsedMs,
          polls: ctx.polls,
          quantity: ctx.quantity,
        },
      );
      const toastId = basketAddToastId(ctx.productId);
      switch (outcome) {
        case "confirmed-late":
          if (ctx.source === "poll") {
            // A poll found the write: the header count is already fresh, so a
            // toast is the least surprising confirmation this long after the
            // click (a modal popping over whatever the shopper is doing now
            // is not).
            toast.success(BASKET_ADD_CONFIRMED_COPY, { id: toastId });
          } else {
            // The late RESPONSE arrived: the layout's fetcher watcher opens the
            // normal added-to-basket modal — just retire our toast.
            toast.dismiss(toastId);
          }
          break;
        case "unconfirmed":
          toast.error(BASKET_ADD_TIMEOUT_COPY, { id: toastId });
          break;
        case "rejected-late": // the fetcher watcher toasts the server's message
        case "abandoned":
          toast.dismiss(toastId);
          break;
      }
    },
    [],
  );
  const addProtocol = useBasketAddReconcile({
    fetcher: basket.fetcher,
    reconcileBasket,
    readLineQuantity,
    onOutcome: onAddOutcome,
    // COM-804: the add answered 503 `upstreamTimeout` before the deadline —
    // same uncertain state, same confirming toast as the deadline path.
    onEnterFromResponse: ({ productId }) => {
      toast.loading(BASKET_ADD_CONFIRMING_COPY, {
        id: basketAddToastId(productId),
      });
    },
  });

  // Pending is DERIVED from the fetcher (never stored), so a remount can't
  // orphan it — but a POST that never settles (COM-67 parked-invocation class /
  // edge stall), or a fetcher whose component-side state never catches up with
  // the router (the recs-popup case), keeps `fetcher.state` busy forever and
  // rendered this button disabled indefinitely ("the loader stays stuck"). The
  // deadline bounds that: past it the span is no longer treated as pending.
  //
  // The PHASE comes from the fetcher's current state, never from `data`
  // (round-1 review finding 1): RR7 keeps the PRIOR submission's payload on the
  // fetcher during a resubmission and `reset()` leaves `null`, so "data
  // present" would have called a hung retry a completed add.
  //   "submit"       (state === "submitting") — the POST itself hung: enter the
  //                  uncertain-state protocol (see `useBasketAddReconcile`).
  //                  NOT reset: the Worker keeps working and the late response
  //                  is the best confirmation we can get.
  //   "revalidation" (state === "loading")    — the add landed and only the
  //                  post-action revalidation is open: re-enable silently (the
  //                  modal already shows the item), beacon only.
  const { timedOut } = useFetcherDeadline(
    basket.fetcher,
    BASKET_ADD_DEADLINE_MS,
    phase => {
      const phaseLabel =
        phase === "submit" ? "submit-timeout" : "revalidation-timeout";
      reportClientError(
        BASKET_ADD_BEACON_SCOPE,
        new Error(
          `basket add did not settle within ${BASKET_ADD_DEADLINE_MS}ms (${phaseLabel})`,
        ),
        {
          productId: productIdForBeacon,
          phase: phaseLabel,
          deadlineMs: BASKET_ADD_DEADLINE_MS,
        },
      );
      if (phase !== "submit") return;
      const entered = addProtocol.enter({
        productId: productIdForBeacon,
        quantity,
        deadlineMs: BASKET_ADD_DEADLINE_MS,
      });
      if (entered) {
        toast.loading(BASKET_ADD_CONFIRMING_COPY, {
          id: basketAddToastId(productIdForBeacon),
        });
      }
    },
  );
  const isAdding = basket.fetcher.state !== "idle" && !timedOut;
  const isConfirming = addProtocol.uncertain;

  const isInStock =
    product.inventory?.ats != null
      ? product.inventory.ats > 0
      : product.available !== false;
  const isMaster = isMasterProduct(product);
  const isBundle = isBundleProduct(product);
  const isSet = isSetProduct(product);
  const isSoldOut = isOutOfStockOverride(product);

  const handleClick = () => {
    // A second add for the same product while the first is still being
    // confirmed is refused, not queued — the button is disabled, and this
    // guard covers a programmatic click.
    if (isConfirming) return;

    // Master products are not orderable — only a resolved variant SKU can be
    // added to the basket. On a tile, route to quick view so the shopper picks
    // a variant. Never fall through to a direct add for a master: SCAPI rejects
    // a master SKU with 422 "Invalid Product Type", which previously hung the
    // cart-page recs carousel (SFCC-11588).
    if (isMaster && isProductTile && !isInQuickView) {
      if (onOpenQuickView) {
        onOpenQuickView(quantity);
      } else {
        console.warn(
          `[AddToCart] Suppressed direct add of master product "${product.id}" — render this tile via RecsCarouselTile (or pass onOpenQuickView) so masters open a quick view.`,
        );
      }
      return;
    }

    // Direct add to cart — standard/variant/bundle/set are already orderable.
    const productId = product.id || product.productId || "";
    // Snapshot the product's basket quantity BEFORE the submit so a poll can
    // tell "landed late" from "was already in the cart".
    addProtocol.arm(productId, quantity);
    // GTM add-context (SFRA `base.js` parity): a tile / quick-view-tile add is
    // clickSource "grid"; `quickView` only inside the QV modal.
    basket.add({ productId }, quantity, {
      clickSource: isProductTile || isInQuickView ? "grid" : "main",
      quickView: isInQuickView,
      uncertainAddOwner: "tile",
    });
  };

  // Button label, split in two: the main part always shows; the extra part
  // wraps to a second (clipped) line when the button is too narrow.
  //   "Add To Cart"     -> "Add"     + "To Cart"
  //   "Add All to Cart" -> "Add All" + "to Cart"
  // Sold-out / out-of-stock states have no extra part.
  let btnTextMain = "Add";
  let btnTextExtra = "To Cart";
  if (isSoldOut) {
    btnTextMain = "Sold Out";
    btnTextExtra = "";
  } else if (!isInStock) {
    btnTextMain = "Out of Stock";
    btnTextExtra = "";
  } else if (isSet) {
    btnTextMain = "Add All";
    btnTextExtra = "to Cart";
  }

  // SFCC-11654: recs-carousel tiles render just "Add" at every width (no
  // wrap-and-clip "To Cart") so the CTA stays legible in the narrow
  // cart-items column. Sold-out / out-of-stock labels are left intact.
  if (compactAddLabel && isInStock && !isSoldOut) {
    btnTextMain = "Add";
    btnTextExtra = "";
  }

  // Uncertain add: the tile must NOT read as "nothing happened". The label
  // says what is going on; the wrap-and-clip layout shows "Confirming" alone
  // when narrow, "Confirming your cart…" when there is room.
  if (isConfirming) {
    btnTextMain = compactAddLabel ? "Confirming…" : "Confirming";
    btnTextExtra = compactAddLabel ? "" : "your cart…";
  }

  const isDisabled = isAdding || isConfirming || isSoldOut || !isInStock;

  return (
    <div>
      <div className="flex gap-2">
        {!hideQuantity && (
          <QuantityStepper
            quantity={quantity}
            onChange={q => {
              setQuantity(q);
              onQuantityChange?.(q);
            }}
            max={resolveQuantityCap(product.inventory?.ats)}
            disabled={isDisabled}
            mode="compact"
            id={`qty-${product.id}`}
            testIdPrefix={
              isProductTile
                ? `product-tile-quantity-${product.id}`
                : "addToCart"
            }
            hideMinus={collapseStepperAtMin}
          />
        )}

        {/* Add to Cart button — takes the rest of the row; the compact stepper
            holds its minimum width beside it. */}
        <div className="flex min-w-0 flex-1">
          <Button
            variant="cta"
            size="5"
            onClick={handleClick}
            disabled={isDisabled}
            aria-busy={isConfirming || undefined}
            data-testid={
              isProductTile
                ? `product-tile-add-to-cart-${product.id}`
                : "addToCart-Button"
            }
            className="grow px-0"
          >
            {collapseStepperAtMin ? (
              // ATC modal (narrow placement, collapseStepperAtMin): the tile is
              // wide enough for the full "Add to Cart" on one line, so render a
              // single non-wrapping label instead of the two-line split/clip
              // used on the narrower PLP/search tiles. truncate is an overflow
              // safety net (e.g. "Add All to Cart" sets) — it never trims the
              // common label. (SFCC-11591)
              <span className="block w-full truncate text-center">
                {btnTextExtra
                  ? `${btnTextMain}\u00A0${btnTextExtra}`
                  : btnTextMain}
              </span>
            ) : fullLabelFromLg ? (
              // Breakpoint-driven: just "Add" below `lg`, full "Add To Cart"
              // from `lg` up (favorites list rows).
              <span className="block w-full truncate text-center">
                {btnTextMain}
                {btnTextExtra && (
                  <span className="hidden lg:inline">
                    {"\u00A0"}
                    {btnTextExtra}
                  </span>
                )}
              </span>
            ) : (
              // Main part stays on its line; the extra part wraps to a second
              // line that h-5 + overflow-hidden clips when the button is too
              // narrow (e.g. "Add To Cart" -> "Add"). Both stay in the DOM so
              // assistive tech reads the full label.
              <div className="flex h-5 flex-wrap justify-center overflow-hidden text-center">
                <span className="whitespace-nowrap">{btnTextMain}</span>
                {btnTextExtra && (
                  <span>
                    {"\u00A0"}
                    {btnTextExtra}
                  </span>
                )}
              </div>
            )}
          </Button>
        </div>
      </div>

      {/* Announce the uncertain state to assistive tech; the visible signal is
          the button label + the loading toast. */}
      {isConfirming && (
        <p role="status" className="sr-only">
          {BASKET_ADD_CONFIRMING_COPY}
        </p>
      )}

      {showStockStatus && isInStock && !isSoldOut && (
        <p className="mt-[.5rem] text-sm text-accent">In Stock</p>
      )}
    </div>
  );
}
